import { GetStaticProps, InferGetStaticPropsType } from 'next';
import type { Thing } from 'schema-dts';
import { getCommonPageProps } from '@/shared/api';
import { getPageDetail } from '@/shared/api/page-detail';
import { getSiteSettings } from '@/shared/api/site-settings';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import Breadcrumbs from '@/shared/components/shared/Breadcrumbs';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import Typografed from '@/shared/components/shared/Typografed';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const PrivacyPage = ({
    h1,
    meta,
    breadcrumbs,
    bodyClass,
    privacyContentProps,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const tpContent = useTypograf(privacyContentProps.text);
    return (
        <DefaultLayout bodyClass={bodyClass}>
            <div className="privacy js-header-theme-trigger" data-theme="dark">
                <div className="privacy-wrapper wrapper">
                    <Breadcrumbs className="privacy-breadcrumbs" breadcrumbs={breadcrumbs} />

                    <div className="privacy-content">
                        <h1 className="privacy-content__title h2">
                            <Typografed>{h1}</Typografed>
                        </h1>
                        <div
                            className="wysiwyg privacy-content__text"
                            dangerouslySetInnerHTML={{ __html: tpContent }}
                        ></div>
                    </div>
                </div>
            </div>
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-privacy"
                data={generateWebPageJsonLd({
                    name: h1,
                    description: meta.description,
                    pagePath: '/privacy/',
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default PrivacyPage;

type PrivacyPageProps = NonUndefined<
    CommonPageProps & {
        h1: string;
        privacyContentProps: PrivacyContentProps;
    }
>;

export type PrivacyContentProps = {
    text: string;
};

export const getStaticProps: GetStaticProps<PrivacyPageProps> = async () => {
    const pageResponse = await getPageDetail({ params: { slug: 'privacy' } });

    const [commonPageProps, siteSettings] = await Promise.all([
        getCommonPageProps(),
        getSiteSettings({
            params: {
                keys: ['privacy_content_text'],
            },
        }),
    ]);

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'privacy-page',
            meta: {
                title: 'Политика конфиденциальности',
                ...(pageResponse.data?.meta ?? []),
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: pageResponse.data?.name ?? 'Политика конфиденциальности', href: '/privacy/' },
            ],
            h1: pageResponse.data?.name ?? 'Политика конфиденциальности',
            privacyContentProps: {
                text: siteSettings.data?.privacy_content_text ?? '',
            },
        } satisfies PrivacyPageProps,
        revalidate: 60,
    };
};
